import React, { useState } from "react";
import { toast } from "react-hot-toast";

// Components
import SubscriptionLimitReached from "../../../shared/components/SubscriptionLimitReached";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import theme from "../../../shared/utils/themeColors";

// Hooks
import { useNavigate } from "react-router-dom";
import { useQuestionBankTests } from "../hooks/useQuestionBankTests";
import { useQuestionCategories } from "../hooks/useQuestionCategories";

// Types
import ModalBlank from "../../../ui/modals/ModalBlank";
import { QuestionCategory } from "../types/question-bank.types";

// Components
const CategoryItem: React.FC<{
  category: QuestionCategory;
  onSelect: (category: QuestionCategory) => void;
  level?: number;
}> = ({ category, onSelect, level = 0 }) => {
  return (
    <div
      className="flex items-center p-2 my-2 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
      onClick={() => onSelect(category)}
    >
      <div
        className={`text-xs px-2 py-1 rounded-full mr-2 min-w-6 text-center ${
          theme.bg.capsule
        } ${category.color ? "text-white" : theme.text.primary}`}
        {...(category.color
          ? { style: { backgroundColor: category.color } }
          : {})}
      >
        {category.totalQuestionCount || 0}
      </div>
      <div className="flex-grow">
        <h3
          className={`${
            level === 0
              ? "font-medium text-gray-800 dark:text-gray-100"
              : "text-sm font-medium text-gray-700 dark:text-gray-300"
          }`}
        >
          {category.name}
        </h3>
        {category.description && level < 2 && (
          <p className="text-xs text-gray-700 dark:text-gray-300 mt-1 line-clamp-1">
            {category.description}
          </p>
        )}
      </div>
    </div>
  );
};

const CategoryTree: React.FC<{
  category: QuestionCategory;
  onSelect: (category: QuestionCategory) => void;
  level?: number;
}> = ({ category, onSelect, level = 0 }) => {
  return (
    <div key={category.id}>
      <CategoryItem category={category} onSelect={onSelect} level={level} />

      {category.subcategories && category.subcategories.length > 0 && (
        <div
          className={`mt-1 ml-3 pl-3 border-l-2 border-gray-200 dark:border-gray-700`}
        >
          {category.subcategories.map((subcategory) => (
            <CategoryTree
              key={subcategory.id}
              category={subcategory}
              onSelect={onSelect}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function CategoryExplorer() {
  const {
    hierarchy,
    loading: categoriesLoading,
    error: categoriesError,
    refresh,
    subscriptionLimitReached,
  } = useQuestionCategories();
  const { generateTest, loading: testsLoading } = useQuestionBankTests();

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] =
    useState<QuestionCategory | null>(null);
  const [questionCount, setQuestionCount] = useState<number>(10);
  const [maxQuestions, setMaxQuestions] = useState<number>(20);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const navigate = useNavigate();

  const renderCategoryHierarchy = (categories: any[], level = 0): any[] => {
    let result: any[] = [];

    categories.forEach((category) => {
      result.push({
        ...category,
        _level: level,
        _indentation: level * 20,
      });

      if (category.subcategories && category.subcategories.length > 0) {
        result = [
          ...result,
          ...renderCategoryHierarchy(category.subcategories, level + 1),
        ];
      }
    });

    return result;
  };

  // Filter hierarchy for display based on search term
  const filteredHierarchy = searchTerm
    ? hierarchy?.filter((mainCategory) => {
        // Check if main category matches search
        const mainMatches =
          mainCategory.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          mainCategory.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());

        // Check if any subcategory matches search
        const hasMatchingSubcategory = mainCategory.subcategories?.some(
          (sub) =>
            sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            sub.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return mainMatches || hasMatchingSubcategory;
      })
    : hierarchy;

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  // Handle refresh
  const handleRefresh = () => {
    refresh();
  };

  // Handle category selection
  const handleCategorySelect = (category: QuestionCategory) => {
    const totalQuestions = category.totalQuestionCount || 0;

    if (totalQuestions === 0) {
      toast.error("No questions available for this category");
      return;
    }

    setSelectedCategory(category);
    setMaxQuestions(totalQuestions);
    setQuestionCount(Math.min(10, totalQuestions));
    setShowGenerateModal(true);
  };

  // Handle generate test
  const handleGenerateTest = async () => {
    if (!selectedCategory) return;

    try {
      setIsGenerating(true);

      // Call the hook to generate the test
      const test = await generateTest(
        selectedCategory.id,
        questionCount,
        `Test from ${selectedCategory.name}`
      );

      if (test) {
        // Navigate to the test page
        toast.success(`Test generated successfully`);
        navigate(`/question-bank/test/${test.id}`);
      } else {
        throw new Error("Failed to generate test");
      }
    } catch (error) {
      console.error("Error generating test:", error);
      toast.error("Failed to generate test. Please try again.");
    } finally {
      setIsGenerating(false);
      setShowGenerateModal(false);
    }
  };

  // If subscription limit is reached, show the subscription limit message
  if (subscriptionLimitReached) {
    return (
      <AuthenticatedLayout defaultCollapsed={true}>
        <SubscriptionLimitReached
          resourceType="question bank categories"
          returnPath="/"
        />
      </AuthenticatedLayout>
    );
  }

  return (
    <>
      <AuthenticatedLayout
        isLoading={categoriesLoading}
        error={categoriesError}
        isEmpty={!hierarchy?.length}
        emptyStateProps={{
          title: "No categories found",
          description: searchTerm
            ? "No categories match your search criteria"
            : "There are no question categories available at the moment",
          icon: (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-gray-200 to-gray-100 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className={`w-8 h-8 ${theme.text.muted}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                />
              </svg>
            </div>
          ),
        }}
        pageHeader={{
          title: "Question Categories",
          refresh: {
            show: true,
            onRefresh: handleRefresh,
          },
        }}
      >
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          {/* Description text */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Select a category to generate a personalized test based on your
              interests and learning needs.
            </p>
          </div>

          {/* Category Hierarchy */}
          <div className="p-0">
            {filteredHierarchy && filteredHierarchy.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredHierarchy.map((mainCategory) => (
                  <div
                    key={mainCategory.id}
                    className="mb-6 p-3 bg-white dark:bg-gray-700 rounded-md"
                  >
                    <CategoryTree
                      category={mainCategory}
                      onSelect={handleCategorySelect}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  No categories available
                </p>
              </div>
            )}
          </div>
        </div>
      </AuthenticatedLayout>

      {/* Generate Test Modal */}
      {showGenerateModal && selectedCategory && (
        <ModalBlank
          id="generate-test-modal"
          modalOpen={showGenerateModal}
          setModalOpen={setShowGenerateModal}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full p-6">
            <h2 className="text-xl font-semibold mb-2 text-gray-800 dark:text-gray-100">
              Generate Test from {selectedCategory.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Select how many questions you want to include in your personalized
              test.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of questions: {questionCount}
              </label>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-500">0</span>
                <input
                  type="range"
                  min="0"
                  max={maxQuestions}
                  value={questionCount}
                  onChange={(e) => setQuestionCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-sm text-gray-500">{maxQuestions}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Maximum available: {maxQuestions} questions
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                type="button"
                className={`${theme.button.secondary}`}
                onClick={() => setShowGenerateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${theme.button.primary}`}
                onClick={handleGenerateTest}
                disabled={questionCount < 1 || isGenerating}
              >
                {isGenerating ? "Generating..." : "Generate Test"}
              </button>
            </div>
          </div>
        </ModalBlank>
      )}
    </>
  );
}

export default CategoryExplorer;
